/** Per-key async mutex: serializes read-modify-write sequences that span awaits (e.g. profile saves). */
export class KeyedMutex {
  private readonly tails = new Map<string, Promise<void>>();

  async run<T>(key: string, fn: () => Promise<T>): Promise<T> {
    const prev = this.tails.get(key) ?? Promise.resolve();
    let release!: () => void;
    const next = new Promise<void>((r) => (release = r));
    const tail = prev.then(() => next);
    this.tails.set(key, tail);
    await prev;
    try {
      return await fn();
    } finally {
      release();
      if (this.tails.get(key) === tail) this.tails.delete(key);
    }
  }

  /**
   * Runs `fn` holding every key, taken one after another in the given order (the caller keeps the services' lock order:
   * services/clans.ts documents it). A key named twice is taken once.
   */
  runAll<T>(keys: readonly string[], fn: () => Promise<T>): Promise<T> {
    const unique = [...new Set(keys)];
    const run = (i: number): Promise<T> => (i >= unique.length ? fn() : this.run(unique[i], () => run(i + 1)));
    return run(0);
  }
}
